<template>
  <section class="p-6 flex flex-col gap-6">
    <!-- Breadcrumb -->
    <nav class="text-gray-600 text-sm">
      <span class="font-semibold cursor-pointer" @click="router.push('/')">Home</span>
      <span class="mx-2">/</span>
      <span class="font-semibold cursor-pointer" @click="router.push({ name: 'Articles' })">Articles</span>
      <span class="mx-2">/</span>
      <span class="text-gray-800 font-bold">Edit Article</span>
    </nav>

    <h3 class="font-bold text-2xl">Edit Article #{{ articleId }}</h3>

    <!-- Errors -->
    <div v-if="errors.length" class="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm">
      <ul class="list-disc list-inside">
        <li v-for="(err, idx) in errors" :key="idx">{{ err }}</li>
      </ul>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-gray-500 text-sm">Loading article data...</div>

    <!-- Form -->
    <form v-else @submit.prevent="submitForm" class="bg-white p-6 rounded shadow space-y-6">
      <!-- Titles -->
      <div class="grid grid-cols-2 gap-4">
        <input v-model="form.title_ar" class="border p-2 rounded" placeholder="Title Arabic" />
        <input v-model="form.title_en" class="border p-2 rounded" placeholder="Title English" />
      </div>

      <!-- Summary -->
      <div class="grid grid-cols-2 gap-4">
        <textarea v-model="form.summary_ar" class="border p-2 rounded" placeholder="Summary Arabic"></textarea>
        <textarea v-model="form.summary_en" class="border p-2 rounded" placeholder="Summary English"></textarea>
      </div>

      <!-- Body -->
      <div class="grid grid-cols-2 gap-4">
        <textarea v-model="form.body_ar" class="border p-2 rounded" rows="5" placeholder="Body Arabic"></textarea>
        <textarea v-model="form.body_en" class="border p-2 rounded" rows="5" placeholder="Body English"></textarea>
      </div>

      <!-- Image -->
      <div>
        <label class="block mb-2 font-medium">Image</label>

        <img v-if="currentImage && !preview" :src="currentImage" class="w-32 h-32 object-cover rounded mb-2" />
        <img v-if="preview" :src="preview" class="w-32 h-32 object-cover rounded mb-2" />

        <input type="file" accept="image/*" @change="onFileChange" />
      </div>

      <!-- Published -->
      <label class="flex gap-2 items-center">
        <input type="checkbox" v-model="form.is_published" />
        Published
      </label>

      <div class="flex justify-end gap-3 mt-4">
        <button type="button" class="px-4 py-2 border rounded-md" @click="router.push({ name: 'Articles' })" :disabled="isSubmitting">
          Cancel
        </button>
        <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60" :disabled="isSubmitting">
          <span v-if="isSubmitting">Saving...</span>
          <span v-else>Update</span>
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/apiClient";
import { useToast } from "vue-toastification";

const route = useRoute();
const router = useRouter();
const toast = useToast();

const FILE_BASE_URL = "http://127.0.0.1:8000";
// const FILE_BASE_URL = import.meta.env.VITE_FILE_BASE_URL || "https://api.transgateacd.com/api";
const articleId = ref(route.params.id);

const loading = ref(false);
const isSubmitting = ref(false);
const errors = ref([]);

const form = ref({
  title_ar: "",
  title_en: "",
  summary_ar: "",
  summary_en: "",
  body_ar: "",
  body_en: "",
  is_published: true,
});

const imageFile = ref(null);
const preview = ref(null);
const currentImage = ref(null);

async function fetchArticle() {
  loading.value = true;
  errors.value = [];
  try {
    const res = await apiClient.get(`/articles/${articleId.value}`);
    const article = res.data?.data ?? res.data; // defensive: handle both shapes

    // If API returns wrapper { data: { article: {...} } } adjust as needed.
    // Here we expect article to be the object itself.
    form.value = {
      title_ar: article.title_ar ?? "",
      title_en: article.title_en ?? "",
      summary_ar: article.summary_ar ?? "",
      summary_en: article.summary_en ?? "",
      body_ar: article.body_ar ?? "",
      body_en: article.body_en ?? "",
      is_published: !!article.is_published,
    };

    if (article.image_url) {
      // تأكّد هذا المسار مطابق للـ API (storage أو files)
      currentImage.value = `${FILE_BASE_URL}/storage/articles/${article.image_url}`;
    } else {
      currentImage.value = null;
    }
  } catch (err) {
    console.error("Fetch article failed:", err);
    errors.value.push("Failed to load article.");
  } finally {
    loading.value = false;
  }
}

onMounted(fetchArticle);

function onFileChange(e) {
  const file = e.target.files?.[0];
  if (!file) {
    imageFile.value = null;
    preview.value = null;
    return;
  }
  imageFile.value = file;
  preview.value = URL.createObjectURL(file);
}

function validateForm() {
  errors.value = [];
  if (!form.value.title_ar.trim() && !form.value.title_en.trim()) {
    errors.value.push("Please enter at least one title (Arabic or English).");
  }
  return errors.value.length === 0;
}

async function submitForm() {
  if (!validateForm()) return;

  isSubmitting.value = true;
  errors.value = [];

  try {
    const fd = new FormData();

    Object.entries(form.value).forEach(([key, val]) => {
      fd.append(key, key === "is_published" ? (val ? "1" : "0") : val ?? "");
    });

    if (imageFile.value) fd.append("image", imageFile.value);

    // ✅ إرسال PUT مباشرة
    await apiClient.post(`/articles/${articleId.value}`, fd);

    toast.success("Article updated successfully!");
    router.push({ name: "Articles" }).then(() => window.location.reload());
  } catch (err) {
    console.error("Update failed:", err);

    if (err.response?.data) {
      const data = err.response.data;
      if (data.message) errors.value.push(data.message);
      if (data.errors) {
        Object.values(data.errors).forEach((arr) => {
          arr.forEach((m) => errors.value.push(m));
        });
      }
    } else {
      errors.value.push("Failed to update article. Please try again.");
    }
  } finally {
    isSubmitting.value = false;
  }
}

</script>

<style scoped>
/* حُط هنا أي ستايل إضافي لو احتجت */
</style>